import { Box, Flex } from '@lego/klik-ui';
import type { ColumnOrderState, RowData } from '@tanstack/react-table';
import type { FC, ReactElement, ReactNode } from 'react';
import { TableHeader } from '../headers/TableHeader';
import type { Column, VisibilityState } from './appTable';
import { TableControl } from './TableControl';
import { renderDescription } from './utils';

type TableToolbarProps<T extends RowData> = {
  addButton?: FC;
  columnOrder: ColumnOrderState;
  columns: Column<T>[];
  columnVisibility: VisibilityState;
  contentPaddingX: number | number[];
  count?: number;
  description?: string | ReactElement;
  hideControls?: boolean;
  isValidating?: boolean;
  refresh?: () => void;
  resetTable: () => void;
  setColumnOrder: (columnOrder: ColumnOrderState) => void;
  setColumnVisibility: (columnVisibility: VisibilityState) => void;
  title?: string;
  toolbarAddon?: ReactNode;
};

export const TableToolbar = <T extends RowData>({
  addButton,
  columnOrder,
  columns,
  columnVisibility,
  contentPaddingX,
  count,
  description,
  hideControls,
  isValidating,
  refresh,
  resetTable,
  setColumnOrder,
  setColumnVisibility,
  title,
  toolbarAddon,
}: TableToolbarProps<T>) => (
  <Flex
    alignItems="center"
    backgroundColor="white"
    direction="row"
    gap="8"
    justifyContent="space-between"
    left="0"
    minW={0}
    paddingX={contentPaddingX}
    paddingY={[3, 5]}
    position="sticky"
    right="0"
    top="0"
    width="100%"
    zIndex={1}
  >
    <Flex direction="column" flex={1} minW={0}>
      {title && <TableHeader count={count} title={title} />}
      {description && <Box minW={0}>{renderDescription(description)}</Box>}
    </Flex>
    {!hideControls && (
      <Box flexShrink={0}>
        <TableControl
          addElement={addButton}
          columnOrder={columnOrder}
          columns={columns}
          columnVisibility={columnVisibility}
          isValidating={isValidating}
          refresh={refresh}
          reset={resetTable}
          setColumnOrder={setColumnOrder}
          setColumnVisibility={setColumnVisibility}
          toolbarAddon={toolbarAddon}
        />
      </Box>
    )}
  </Flex>
);
